import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { quotaApi } from '../../api/quota';
import { getPortalUrl, clearDevPortalOverride } from '../../lib/portal';
import { track, EVENTS } from '../../lib/mixpanel';
import { SettingsModal } from './SettingsModal';
import { QuotaDetailModal } from './QuotaDetailModal';
import { LegalModal } from './LegalModal';
import { NavbarUI } from './NavbarUI';
import { NotificationBell } from '../Notifications/NotificationBell';
import type { QuotaUsage } from '../../types';

interface NavbarProps {
  portal?: 'creator' | 'viewer';
}

export const Navbar: React.FC<NavbarProps> = ({ portal }) => {
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [quotaOpen, setQuotaOpen] = useState(false);
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);
  const [quotaUsage, setQuotaUsage] = useState<QuotaUsage | null>(null);
  const [navHidden, setNavHidden] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const lastScrollY = useRef(0);

  const isGallery = location.pathname.includes('/gallery');
  const showQuota = portal !== 'viewer' && user && user.betaApproved && !user.isOwner;

  const handleCreatorModeAction = () => {
    track(EVENTS.CREATOR_MODE_CLICKED, { portal, waitlistStatus: user?.waitlistStatus });
    if (portal === 'creator') {
      clearDevPortalOverride();
      window.location.href = getPortalUrl('viewer');
      return;
    }
    if (user?.betaApproved || user?.isOwner) {
      window.location.href = getPortalUrl('creator');
      return;
    }
    // pending — button is disabled in NavbarUI, shouldn't reach here
    // null or rejected → go to become-a-creator page
    navigate('/become-a-creator');
  };

  // Fetch quota for beta users (skip on viewer portal)
  useEffect(() => {
    if (!showQuota) return;
    quotaApi.getMyQuota().then(setQuotaUsage).catch(() => {/* non-critical */});
  }, [showQuota]);

  // Hide on scroll-down, reveal on scroll-up — gallery pages only
  useEffect(() => {
    if (!isGallery) {
      setNavHidden(false); // eslint-disable-line react-hooks/set-state-in-effect
      return;
    }
    const onScroll = () => {
      const current = window.scrollY;
      const delta = current - lastScrollY.current;
      if (current < 80) {
        setNavHidden(false);
      } else if (delta > 6) {
        setNavHidden(true);
      } else if (delta < -6) {
        setNavHidden(false);
      }
      lastScrollY.current = current;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isGallery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <NavbarUI
        user={user}
        pathname={location.pathname}
        theme={theme}
        logoLoaded={logoLoaded}
        dropdownOpen={dropdownOpen}
        hidden={navHidden}
        quotaUsage={quotaUsage}
        portal={portal}
        notificationsSlot={user ? <NotificationBell /> : null}
        onLogoLoad={() => setLogoLoaded(true)}
        onToggleDropdown={() => setDropdownOpen((o) => !o)}
        onCloseDropdown={() => setDropdownOpen(false)}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenQuota={() => { setDropdownOpen(false); setQuotaOpen(true); }}
        onLogout={logout}
        onThemeChange={setTheme}
        onLegalOpen={(type) => { setDropdownOpen(false); setLegalModal(type); }}
        onCreatorModeAction={handleCreatorModeAction}
        onNavigateAdmin={() => { setDropdownOpen(false); navigate('/admin'); }}
        dropdownRef={dropdownRef}
      />
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <QuotaDetailModal isOpen={quotaOpen} onClose={() => setQuotaOpen(false)} quota={quotaUsage} />
      {legalModal && <LegalModal type={legalModal} isOpen onClose={() => setLegalModal(null)} />}
    </>
  );
};
